import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Container,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Slide,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_ACTIVE_POLICY_LIST,
  ACKNOWLEDGE_POLICY,
} from "../../../../apis/settingsApis/policy-apis/policyApis";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import BackButton from "../../../../components/BackButton/BackButton";

const EmployeePolicyAck = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const policyId = queryParams.get("id");

  const { loading, data, refetch } = useQuery(GET_ACTIVE_POLICY_LIST);
  const [acknowledgePolicyMutation] = useMutation(ACKNOWLEDGE_POLICY);

  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const policy = data?.getAllTypePolicies?.activePolicies?.find(
    (p) => Number(p.policyId) === Number(policyId)
  );

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    try {
      const response = await acknowledgePolicyMutation({
        variables: {
          file_id: policy.fileId,
          ack: true,
        },
      });

      if (response.data?.updatePolicyEmployeeAck?.success) {
        setSnackbarInfo({
          message: "Policy acknowledged successfully!",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
        await refetch();
      } else {
        throw new Error(
          response.data?.updatePolicyEmployeeAck?.message ||
          "Failed to acknowledge policy"
        );
      }
    } catch (error) {
      setSnackbarInfo({
        message: error.message || "Failed to acknowledge policy",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } finally {
      setIsAcknowledging(false);
    }
  };

  const handleScrolledToBottom = () => {
    setScrolledToBottom(true);
  };

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!policy) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ height: "75vh" }}
      >
        Policy not found
      </Box>
    );
  }

  const isAcknowledged = policy?.ack;

  return (
    <Box sx={{ maxWidth: "1000px", margin: "0 auto", pb: 4 }}>
      <BackButton label="Back to Policies" sx={{ mb: 2 }} />

      <Card
        variant="outlined"
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.customColors?.border?.strong || "#E0E0E0"}`,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor:
              theme.customColors?.background?.cardHeader || "#F7F7FC",
            borderBottom: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <DescriptionOutlinedIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: 28,
              }}
            />
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong || "#1A1A2E",
                  letterSpacing: "-0.01em",
                }}
              >
                {policy?.policyDisplayName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors?.text?.secondary || "#6B7280",
                  mt: 0.25,
                }}
              >
                {isAcknowledged
                  ? "You have acknowledged this policy."
                  : "Please read the policy carefully before acknowledging."}
              </Typography>
            </Box>
          </Box>
          {isAcknowledged && (
            <Chip
              label="Acknowledged"
              size="small"
              sx={{
                backgroundColor: "#E8FBF3",
                color: "#07902E",
                fontWeight: "500",
              }}
            />
          )}
        </Box>

        {/* Document */}
        <CardContent sx={{ px: 3, py: 2.5 }}>
          {!isAcknowledged && (
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                mb: 3,
                backgroundColor:
                  theme.customColors?.background?.info || "#F8F9FA",
                border: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors?.text?.secondary || "#6B7280",
                  lineHeight: 1.6,
                  fontStyle: "italic",
                }}
              >
                <Box
                  component="strong"
                  sx={{
                    fontWeight: 600,
                    fontStyle: "normal",
                    color: theme.customColors?.text?.strong || "#1A1A2E",
                  }}
                >
                  Note:
                </Box>{" "}
                Please scroll through and read the entire document below. The
                acknowledgement checkbox will be enabled once you reach the
                bottom.
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              borderRadius: "10px",
              overflow: "hidden",
              border: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
              height: "calc(100vh - 380px)",
              mb: 3,
            }}
          >
            <Box sx={{ width: "100%", height: "100%" }}>
              <PdfViewer
                fileUrl={`${process.env.REACT_APP_CLOUDFRONT_URL}/${policy?.filePath?.toLowerCase().endsWith(".docx") ? policy.filePath.replace(/\.docx$/i, ".pdf") : policy?.filePath}`}
                onScrolledToBottom={handleScrolledToBottom}
              />
            </Box>
          </Box>

          {/* Acknowledgement Section */}
          {!isAcknowledged && (
            <Box
              sx={{
                pt: 2.5,
                borderTop: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasRead}
                    disabled={!scrolledToBottom}
                    onChange={(e) => setHasRead(e.target.checked)}
                    sx={{
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I have read and understood this policy, and I agree to
                    comply with it.
                  </Typography>
                }
              />
              <LoadingButton
                variant="contained"
                loading={isAcknowledging}
                onClick={handleAcknowledge}
                disabled={!hasRead}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                }}
              >
                Acknowledge Policy
              </LoadingButton>
            </Box>
          )}
        </CardContent>
      </Card>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default EmployeePolicyAck;
